import { SolidBlock } from "@/app/components/brand-scaffold";
import {
  heroColor,
  gridColors,
  neutralColors,
  colorRules,
  productLayerMappings,
  accentColors,
} from "@/design-system/registry/playground-palette";

export function PlaygroundPaletteSection() {
  return (
    <div>
      <div className="border-l-8 border-black pl-4 mb-10">
        <p className="text-xs font-black uppercase tracking-widest text-zinc-400 mb-1">SECTION E</p>
        <h2 className="text-3xl font-black uppercase">品牌配色规范</h2>
      </div>
      <p className="font-bold text-zinc-500 mb-10 text-sm">全站配色由两套系统构成：一套是高饱和度的 <strong>9 色品牌调色板</strong>（用于组件背景、语义标注），另一套是极简的 <strong>中性基底色</strong>（用于页面底层和文字）。</p>

      {/* E-1: 9 色品牌调色板 */}
      <div className="mb-16">
        <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【E.1】品牌 9 色调色板</h3>
        <div className="flex h-[320px] border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          {/* Green — full height left column */}
          <div className="w-1/5 flex-shrink-0 border-r-4 border-black flex flex-col h-full">
            <div className="flex-1" style={{ backgroundColor: heroColor.hex }} />
            <div className="p-3 bg-white border-t-4 border-black">
              <p className="font-black uppercase text-sm">{heroColor.name}</p>
              <p className="text-xs font-bold text-zinc-500 font-mono">{heroColor.hex}</p>
              <p className={`text-xs font-black ${heroColor.semanticTextClass} ${heroColor.semanticBgClass} px-1 mt-1 inline-block`}>{heroColor.semantic}</p>
            </div>
          </div>
          {/* Right: 4×2 grid */}
          <div className="flex-1 grid grid-cols-4 grid-rows-2 h-full">
            {gridColors.map((c, i) => (
              <div key={c.hex} className={`flex flex-col ${i < 4 ? "border-b-4" : ""} ${(i % 4) < 3 ? "border-r-4" : ""} border-black`}>
                <div className="flex-1" style={{ backgroundColor: c.hex }} />
                <div className="p-3 bg-white border-t-4 border-black">
                  <p className="font-black uppercase text-sm">{c.name}</p>
                  <p className="text-xs font-bold text-zinc-500 font-mono">{c.hex}</p>
                  <p className={`text-xs font-black ${c.semanticTextClass} ${c.semanticBgClass} px-1 mt-1 inline-block`}>{c.semantic}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* E-2: 中性基底色 */}
      <div className="mb-16">
        <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【E.2】中性基底色</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          {neutralColors.map((c, i) => (
            <div key={c.hex} className={i < neutralColors.length - 1 ? "border-r-4 border-black" : "border-black"}>
              <div className={`h-24 ${c.bgClass}${c.needsBorder ? " border-b-4 border-black" : ""}`} />
              <div className="p-3 bg-white border-t-4 border-black">
                <p className="font-black uppercase text-sm">{c.name}</p>
                <p className="text-xs font-bold text-zinc-500 font-mono">{c.hex}</p>
                <p className="text-xs font-bold text-zinc-600">{c.usage}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* E-3: 配色使用规则 */}
      <div>
        <h3 className="text-2xl font-black uppercase mb-6 border-b-4 border-black pb-2 inline-block">【E.3】配色使用规则</h3>
        <div className="grid md:grid-cols-2 gap-6">
          {colorRules.map((r) => (
            <SolidBlock key={r.number} bgClass={r.bgClass}>
              <p className={`text-xs font-black uppercase tracking-widest ${r.labelClass} mb-3`}>规则 {r.number}</p>
              <h4 className="text-xl font-black uppercase mb-3">{r.title}</h4>
              <p className={r.bgClass.includes("text-white") ? "text-sm font-bold text-zinc-400" : "text-sm font-bold text-black/80"}>{r.description}</p>
            </SolidBlock>
          ))}
        </div>
      </div>

      {/* E-4: 产品层 × 色词映射 */}
      <div className="mt-16">
        <h3 className="text-2xl font-black uppercase mb-2 border-b-4 border-black pb-2 inline-block">【E.4】产品层 × 色词映射</h3>
        <p className="font-bold text-zinc-500 mb-8 mt-4 text-sm">
          遇到需要选色的场景时，先对照此表确认内容所属的产品层，再取对应色词。只有当内容不属于任何产品层时，才从"装饰辅助色"中自由选取。
        </p>

        <div className="space-y-3 mb-10">
          <div className="grid grid-cols-[140px_1fr_1fr] gap-0 text-xs font-black uppercase tracking-widest text-zinc-400 pb-2 border-b-2 border-black/20 px-2">
            <span>产品层</span>
            <span>指定色</span>
            <span>适用内容</span>
          </div>

          {productLayerMappings.map((m) => (
            <div key={m.layer} className="grid grid-cols-[140px_1fr_1fr] gap-0 border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] items-stretch">
              <div className={`${m.layerBg} p-4 flex items-center border-r-4 border-black`}>
                <span className={`font-black uppercase text-sm ${m.layerTextClass}`}>{m.layer}</span>
              </div>
              <div className="bg-white p-4 flex items-center gap-3 border-r-4 border-black">
                <span className="w-5 h-5 border-2 border-black flex-shrink-0 inline-block" style={{ backgroundColor: m.hex }} />
                <span className="font-black text-sm">{m.colorName}<span className="font-mono text-zinc-400 ml-2 text-xs">{m.hex}</span></span>
              </div>
              <div className="bg-white p-4">
                <p className="text-xs font-bold text-zinc-600">{m.usage}</p>
              </div>
            </div>
          ))}
        </div>

        {/* 装饰辅助色 */}
        <div className="border-4 border-dashed border-black/30 p-6 bg-zinc-50">
          <p className="text-sm font-black uppercase mb-4 text-black border-l-4 border-black pl-2">装饰辅助色（仅当内容不属于任何产品层时使用）</p>
          <div className="flex flex-wrap gap-4">
            {accentColors.map((c) => (
              <div key={c.hex} className="flex items-center gap-2 border-4 border-black px-4 py-2 bg-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                <span className="w-4 h-4 border-2 border-black inline-block" style={{ backgroundColor: c.hex }} />
                <span className="font-black text-sm">{c.name} <span className="font-mono text-zinc-400 text-xs">{c.hex}</span></span>
                {c.note && <span className="text-xs font-black text-white px-1" style={{ backgroundColor: c.hex }}>{c.note}</span>}
              </div>
            ))}
          </div>
          <p className="text-xs font-bold text-zinc-500 mt-4">Red 虽也可作装饰用，但优先级最低——一旦场景有破坏性操作（删除/错误/警告），Red 必须留给该语义。</p>
        </div>
      </div>
    </div>
  );
}
